/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    Component,
    ComponentFactoryResolver,
    Inject,
    Type,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {Observable} from 'rxjs';
import $ from 'jquery';
import {
    DefaultFcResponsiveAgDialogOptionConfig,
    FcResponsiveAgDialogOptions,
    FcResponsiveAgDialogOptionsOverrides
} from './fc-responsive-ag-dialog-options';
import {FcResponsiveAgDialogWrapperComponent} from './fc-responsive-ag-dialog-wrapper.component';
import {FcResponsiveAgDialogComponent} from './fc-responsive-ag-dialog.component';

/**
 * View container manager which manages a list of modals currently active
 * inside the viewContainer.
 */
@Component({
    selector: 'fc-responsive-ag-dialog-holder',
    templateUrl: './fc-responsive-ag-dialog-holder.component.html',
    styleUrls: ['./fc-responsive-ag-dialog-holder.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FcResponsiveAgDialogHolderComponent {
    /**
     * Target viewContainer to insert modals.
     */
    @ViewChild('viewContainer', {read: ViewContainerRef, static: true}) viewContainer : any;

    /**.
     * Modal collection, maintained by addModal and removeModal
     *
     * @type {Array<FcResponsiveAgDialogComponent> }
     */
    modals: Array<FcResponsiveAgDialogComponent> = [];

    /**
     * Component constructor.
     *
     * @param resolver Component Factory Resolver.
     * @param defaultFcResponsiveAgDialogOptions Dialog options.
     */
    constructor(private resolver: ComponentFactoryResolver,
                @Inject(DefaultFcResponsiveAgDialogOptionConfig) private defaultFcResponsiveAgDialogOptions: 
                    FcResponsiveAgDialogOptions) {
    }

    /**
     * Configures then adds modal to the modals array, and populates with data passed in.
     *
     * @param component Component instance.
     * @param data Data.
     * @param options Options.
     * @returns Observable observable. 
     * @example
     */
    addModal(component: Type<FcResponsiveAgDialogComponent>,
        data ?: any,
        options ?: FcResponsiveAgDialogOptionsOverrides): Observable<any> {
        // create component
        const factory = this.resolver.resolveComponentFactory(FcResponsiveAgDialogWrapperComponent);
        const componentRef = this.viewContainer.createComponent(factory);
        const modalWrapper: FcResponsiveAgDialogWrapperComponent = <FcResponsiveAgDialogWrapperComponent>(
            componentRef.instance
        );
        
        const {ref: _componentRef, component: _component} = modalWrapper.addComponent(component);

        // assign options refs
        _component.options = options = Object.assign({}, 
            this.defaultFcResponsiveAgDialogOptions, options) as FcResponsiveAgDialogOptions;

        // set base classes for wrapper
        if (options.wrapperDefaultClasses) {
            modalWrapper.modalClasses = options.wrapperDefaultClasses;   
        }

        // add to stack
        this.modals.push(_component);

        if (_component.options) {
            $(modalWrapper.wrapper.nativeElement).toggleClass(_component.options.wrapperClass);
            $('body').addClass(_component.options.bodyClass);
        }

        // when closing modal remove it
        _component.onClosing(modal => this.removeModal(modal));

        // map and return observable
        _component.mapDataObject(data);

        return _component.setupObserver();
    }

    /**
     * Triggers components close function to take effect.
     * 
     * @param closingModal Closing modal object.
     */
    removeModal(closingModal: FcResponsiveAgDialogComponent): void {
        const options = closingModal.options;
        
        if (this.modals.length === 1) {
            $('body').removeClass(options.bodyClass);
        }
        
        if (options) {
            $(closingModal.wrapper.nativeElement).toggleClass(options.wrapperClass);
            this.removeModalFromArray(closingModal);
        }
    }

    /**
     * Remove all modals instance.
     *
     * @returns Promise Promise object.
     */
    removeAllModals(): Promise<any> {
        return Promise.all(this.modals.map(modal => this.removeModal(modal)));
    }
    
    /**
     * Remove component in array.
     * 
     * @param component Component instance.
     */
    removeModalFromArray(component:any): void {
        const index = this.modals.indexOf(component);
        if (index > -1) {
            this.viewContainer.remove(index);
            this.modals.splice(index, 1);
        }
    }
}